import { VirtualMcSettings } from '@/src/components/events/eventDetailsSections/Summary/VirtualMcSettings';
import { config } from '@/src/config/app-config';
import { RoutePath } from '@/src/RoutePath';
import { EditEventActions, useEditEvent } from '@/src/store/edit-event.context';
import { useUser } from '@/src/store/user.context';
import { VirtualMcSource } from '@/src/utils/engagement/engagement-tracker';
import { Container, Grid, Header, SpaceBetween } from '@amzn/awsui-components-react';
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Event } from '../../../types/Event';
import { i18nKeys } from '../../../utils/i18n.utils';
import AccessControl from '../../common/CampaignEventComponents/AccessControl';
import Gamified from '../../common/CampaignEventComponents/Gamified';
import RankingStrategy from '../../common/CampaignEventComponents/RankingStrategy';
import LabAvailabilty from '../../common/CampaignEventComponents/LabAvailability';
import Scoring from '../../common/CampaignEventComponents/Scoring';
import SkillBuilderEventSettings from '../../common/CampaignEventComponents/SkillBuilder';
import Link from '../../ui/atoms/Link/Link';
import ParticipantRegistration from './AdvancedSettings/ParticipantRegistration';
import Sponsorship from './AdvancedSettings/Sponsorship';
import AITeammateSettings from '@/src/components/events/eventDetailsSections/Summary/AITeammateSettings';

interface EventDetailsAdvancedSettingsProps {
  event: Event;
  checkValid: MutableRefObject<() => boolean>;
}

const EventDetailsAdvancedSettings: React.FC<EventDetailsAdvancedSettingsProps> = ({ event, checkValid }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { editMode, handleUpdateEditEvent } = useEditEvent();

  const checkValidSettings = {
    virtualMcSettings: useRef(() => true),
  };

  useEffect(() => {
    checkValid.current = () => Object.values(checkValidSettings).every((check) => check.current());
  });

  return (
    <SpaceBetween direction="vertical" size="s">
      <Container>
        <Grid gridDefinition={[{ colspan: 8 }]}>
          <Header
            variant="h2"
            description={
              <div>
                {t(i18nKeys.events.eventDetails.headers.advancedSettings.description)}
                <Link href={RoutePath.FAQ}>FAQ</Link>
              </div>
            }
          >
            {t(i18nKeys.events.eventDetails.headers.advancedSettings.header)}
          </Header>
        </Grid>
      </Container>
      <Scoring target={event} />
      {/* <ChimeNotifications target={event} /> */}
      <Gamified target={event} />
      <RankingStrategy target={event} />
      {config.ff.enableVirtualMc && (
        <VirtualMcSettings
          virtualMcEnabled={event.virtualMcEnabled}
          setVirtualMcEnabled={(enabled) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_ENABLED, enabled)}
          virtualMcVoice={event.virtualMcVoice}
          setVirtualMcVoice={(voice) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_VOICE, voice)}
          virtualMcFrequency={event.virtualMcFrequency}
          setVirtualMcFrequency={(frequency) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_FREQUENCY, frequency)}
          checkValid={checkValidSettings.virtualMcSettings}
          disabled={!editMode}
          expandable
          source={VirtualMcSource.ADVANCED_SETTINGS}
        />
      )}
      {config.ff.enableAiTeammate && user?.isSuperAdmin && (
        <AITeammateSettings
          target={event}
          disabled={!editMode}
          setAiTeammateEnabled={(enabled) => handleUpdateEditEvent(EditEventActions.AI_TEAMMATE_ENABLED, enabled)}
        />
      )}
      {/* Removing PublicEventSettings as part of https://sim.amazon.com/issues/JAM-13572 because it does not currently work.*/}
      {/* {user?.isSuperAdmin && <PublicEventSettings event={event} />} */}
      <SkillBuilderEventSettings target={event} />
      {/* {user?.isSuperAdmin && <PreEventLobbyVideo event={event} />} */}
      <ParticipantRegistration event={event} />
      <AccessControl target={event} />
      {user?.isSuperAdmin && <LabAvailabilty target={event} />}
      {user?.isSuperAdmin && <Sponsorship event={event} />}
      {/* <EventCapacityRedirectSettings event={event} /> */}
    </SpaceBetween>
  );
};

export default EventDetailsAdvancedSettings;
